import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "../../../../lib/api.js";
import { supportApi } from "../../client.js";

/**
 * Staff "Create ticket" modal. Staff users on the dashboard pick a
 * customer organisation, optionally a reporter (one of that org's
 * contacts), set the title + description, and submit. The Rails
 * CreateTicket service accepts `organization_id` + `reporter_id`
 * from staff actors and validates the linkage server-side.
 */
export function NewTicketModal({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [orgQuery, setOrgQuery] = useState("");
  const [orgId, setOrgId] = useState<number | null>(null);
  const [reporterId, setReporterId] = useState<number | null>(null);
  const [priorityId, setPriorityId] = useState<number | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  // Org typeahead — debounced via React Query's enabled flag.
  const orgs = useQuery({
    queryKey: ["support", "create-ticket", "orgs", orgQuery],
    queryFn: () =>
      supportApi.listOrganizations(orgQuery ? { q: orgQuery, per_page: "20" } : { per_page: "20" }),
    staleTime: 30_000,
  });

  const contacts = useQuery({
    queryKey: ["support", "create-ticket", "contacts", orgId],
    queryFn: () => supportApi.listOrgContacts(orgId!),
    enabled: !!orgId,
    staleTime: 30_000,
  });

  const priorities = useQuery({
    queryKey: ["support", "priorities"],
    queryFn: supportApi.listPriorities,
    staleTime: 5 * 60_000,
  });

  // Reset reporter when the org changes (a contact tied to a different
  // org is rejected by Rails' CreateTicket service).
  useEffect(() => {
    setReporterId(null);
  }, [orgId]);

  const create = useMutation({
    mutationFn: () =>
      supportApi.createTicket({
        title: title.trim(),
        organization_id: orgId!,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(reporterId ? { reporter_id: reporterId } : {}),
        ...(priorityId ? { priority_id: priorityId } : {}),
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["support", "tickets"] });
      onClose();
      navigate(`/support/tickets/${res.data.id}`);
    },
  });

  const submitting = create.isPending;
  const canSubmit = !!orgId && title.trim().length > 0 && !submitting;

  return (
    <Dialog
      open
      onClose={() => { if (!submitting) onClose(); }}
      maxWidth="sm"
      fullWidth
      aria-label="Create ticket"
    >
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) create.mutate();
        }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          New ticket
          <Typography sx={{ fontSize: 14, fontWeight: 400, color: "text.disabled" }}>
            File a ticket on behalf of a customer organisation.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: "grid", gap: 2 }}>
            <Field label="Organisation" required>
              <TextField
                value={orgQuery}
                onChange={(e) => {
                  setOrgQuery(e.target.value);
                  const match = orgs.data?.data.find(
                    (o) => o.name.toLowerCase() === e.target.value.toLowerCase(),
                  );
                  setOrgId(match?.id ?? null);
                }}
                placeholder="Search organisations…"
                required
                fullWidth
                inputProps={{ list: "org-suggestions" }}
              />
              <Box component="datalist" id="org-suggestions">
                {orgs.data?.data.map((o) => (
                  <option key={o.id} value={o.name} />
                ))}
              </Box>
            </Field>

            <Field
              label="Reporter (contact)"
              {...(orgId ? {} : { hint: "Pick an organisation first." })}
            >
              <TextField
                select
                value={reporterId === null ? "" : String(reporterId)}
                onChange={(e) => setReporterId(e.target.value ? Number(e.target.value) : null)}
                disabled={!orgId || contacts.isLoading}
                fullWidth
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">— You (file as yourself) —</MenuItem>
                {contacts.data?.data.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {[c.first_name, c.last_name].filter(Boolean).join(" ") || c.email} ·{" "}
                    {c.email}
                  </MenuItem>
                ))}
              </TextField>
            </Field>

            <Field label="Priority">
              <TextField
                select
                value={priorityId === null ? "" : String(priorityId)}
                onChange={(e) => setPriorityId(e.target.value ? Number(e.target.value) : null)}
                fullWidth
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">— Default —</MenuItem>
                {priorities.data?.data.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Field>

            <Field label="Title" required>
              <TextField
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. CSV import returns 500"
                required
                fullWidth
              />
            </Field>

            <Field label="Description">
              <TextField
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={5}
                placeholder="What did the customer report? Steps to reproduce…"
                fullWidth
              />
            </Field>

            {create.isError && (
              <Typography
                sx={{
                  borderRadius: 1,
                  border: 1,
                  borderColor: (t) => alpha(t.palette.error.main, 0.4),
                  bgcolor: (t) => alpha(t.palette.error.main, 0.05),
                  px: 1.5,
                  py: 1,
                  fontSize: 12,
                  color: "error.main",
                }}
              >
                {create.error instanceof ApiError
                  ? create.error.message
                  : "Could not create ticket."}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button type="button" variant="text" color="inherit" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            {submitting ? "Creating…" : "Create ticket"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box component="label" sx={{ display: "grid", gap: 0.5 }}>
      <Box component="span" sx={{ fontSize: 12, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
        {label}
        {required && <Box component="span" sx={{ ml: 0.25, color: "error.main" }}>*</Box>}
      </Box>
      {children}
      {hint && (
        <Box component="span" sx={{ fontSize: 12, color: "text.disabled" }}>{hint}</Box>
      )}
    </Box>
  );
}
